import {
  generateTempClipId,
  writeQueueService,
} from '@src/services/WriteQueueService';
import { makeAutoObservable, runInAction } from 'mobx';

import type { RootStore } from './RootStore';
import type {
  LightweightVideoDTO,
  QueuedClipSave,
  QueueStatus,
} from '@src/services/WriteQueueService';

/**
 * MobX store that wraps the WriteQueueService to provide reactive state
 * for the UI. This enables components to observe queue status changes
 * and react accordingly.
 */
export class WriteQueueStore {
  // Observable state
  status: QueueStatus = {
    pending: 0,
    saving: 0,
    saved: 0,
    failed: 0,
    items: [],
    isProcessing: false,
  };

  private unsubscribe: (() => void) | null = null;
  private initialized = false;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);

    // Subscribe to status changes from the service
    this.unsubscribe = writeQueueService.onStatusChange(newStatus => {
      runInAction(() => {
        this.status = newStatus;
      });
    });
  }

  /**
   * Initialize the store with repositories from DatoClientStore.
   * Should be called after DatoClientStore is initialized.
   *
   * Note: We use baseVideoRepository (not videoRepository) to avoid infinite
   * recursion when CachedVideoRepository is enabled. The cache delegates to
   * WriteQueueService, which needs to call the actual Dato API.
   *
   * IMPORTANT: Only initializes if valid user authentication is available.
   * Without proper auth, writes will fail with 401 errors.
   */
  initialize(): void {
    if (this.initialized) return;

    const { datoClientStore } = this.rootStore;

    // Don't initialize if we don't have valid user auth
    // The fallback env var token may not have write permissions
    if (!datoClientStore.hasValidUserAuth) {
      console.debug(
        'WriteQueueStore: skipping initialization - no valid user auth',
      );
      return;
    }

    const { baseVideoRepository, storyRepository } = datoClientStore;
    if (baseVideoRepository && storyRepository) {
      writeQueueService.setRepositories(baseVideoRepository, storyRepository);
      this.initialized = true;

      // Sync initial status
      runInAction(() => {
        this.status = writeQueueService.getStatus();
      });
    }
  }

  /**
   * Re-initialize after authentication becomes available.
   * Call this when user logs in or auth state changes.
   */
  reinitialize(): void {
    this.initialized = false;
    this.initialize();
  }

  // --- Computed Properties ---

  get hasPendingItems(): boolean {
    return this.status.pending > 0 || this.status.saving > 0;
  }

  get hasFailedItems(): boolean {
    return this.status.failed > 0;
  }

  get totalActive(): number {
    return this.status.pending + this.status.saving;
  }

  get isIdle(): boolean {
    return (
      !this.status.isProcessing &&
      this.status.pending === 0 &&
      this.status.saving === 0
    );
  }

  // --- Actions ---

  /**
   * Enqueue clips for background saving.
   * Returns the temp IDs for each clip.
   */
  enqueueClips(
    clips: Array<{
      id?: string; // Optional pre-generated temp ID
      video: LightweightVideoDTO;
      storyId: string;
      onComplete?: (realId: string, slug: string, hash: string) => void;
      onError?: (error: Error) => void;
    }>,
  ): string[] {
    const itemsToEnqueue = clips.map(clip => ({
      id: clip.id ?? generateTempClipId(),
      video: clip.video,
      storyId: clip.storyId,
      onComplete: clip.onComplete,
      onError: clip.onError,
    }));

    writeQueueService.enqueue(itemsToEnqueue);

    return itemsToEnqueue.map(item => item.id);
  }

  /**
   * Check if a clip is currently queued (by temp ID)
   */
  isClipQueued(tempId: string): boolean {
    return writeQueueService.isQueued(tempId);
  }

  /**
   * Wait for a specific clip to be saved
   */
  async waitForClip(
    tempId: string,
  ): Promise<{ realId: string } | { error: string }> {
    return writeQueueService.waitForItem(tempId);
  }

  /**
   * Get a queued item by temp ID
   */
  getQueuedItem(tempId: string): QueuedClipSave | undefined {
    return this.status.items.find(item => item.id === tempId);
  }

  /**
   * Retry all failed saves
   */
  retryFailed(): void {
    writeQueueService.retryFailed();
  }

  /**
   * Discard all failed saves
   */
  discardFailed(): void {
    writeQueueService.discardFailed();
  }

  /**
   * Clear completed items from the queue
   */
  clearCompleted(): void {
    writeQueueService.clearCompleted();
  }

  /**
   * Flush all pending items (for use before page unload)
   */
  async flush(): Promise<void> {
    return writeQueueService.flush();
  }

  /**
   * Cleanup when store is destroyed
   */
  destroy(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    writeQueueService.destroy();
  }
}
